import { useEffect, useRef } from 'react'

import { useAuthState } from '@percent/cause-dashboard/common/hooks/useAuthState/useAuthState'
import { BACKEND_POSTHOG_FEATURE_FLAGS } from '../useBackendFeatureFlag/backendPosthogFeatureFlags'
import { useBackendFeatureFlagVariant } from '../useBackendFeatureFlag/useBackendFeatureFlag'
import { useCausesPortalAnalytics } from '../useCausesDashboardAnalytics/useCausesDashboardAnalytics'
import {
  GFM_EXPERIMENT_EVENTS,
  GFM_EXPERIMENT_NAME,
  setGfmExperimentVariant,
  toGfmExperimentVariant
} from './gfmExperiment'

export const GFM_EXPOSURE_SESSION_KEY = 'gfm_experiment_exposed'

// Scoped per account: sessionStorage survives logout, so two users in one tab who land in the
// same arm would otherwise share a single exposure between them.
const exposureKey = (accountId: string | undefined, variant: string) => `${accountId ?? 'anonymous'}:${variant}`

const hasAlreadyFired = (key: string) => {
  try {
    return sessionStorage.getItem(GFM_EXPOSURE_SESSION_KEY) === key
  } catch {
    // Private browsing or blocked storage: fire rather than stay silent, a duplicate exposure
    // is easier to live with in Metabase than a missing one.
    return false
  }
}

const recordFired = (key: string) => {
  try {
    sessionStorage.setItem(GFM_EXPOSURE_SESSION_KEY, key)
  } catch {
    // nothing to do; the in-memory ref still stops a repeat within this page load
  }
}

/*
  NGM-180. Resolves the master flag, publishes the variant so every later Segment call carries it
  in context, and fires `Experiment Exposed` once per account per session for all three arms.
  Mounted once, at the app root.
*/
export function useGfmExperimentExposure() {
  const { authState } = useAuthState()
  const accountId = authState.user?.id
  const { ready, value } = useBackendFeatureFlagVariant(BACKEND_POSTHOG_FEATURE_FLAGS.GOFUNDME_USER_JOURNEYS_AB_TEST)
  const { track } = useCausesPortalAnalytics()

  // useAnalytics returns a new object each render, so `track` is a new identity each render too.
  // Holding it in a ref keeps this effect driven by the flag rather than by every render of App.
  const trackRef = useRef(track)
  trackRef.current = track

  const firedFor = useRef<string | null>(null)

  /*
    Published during render, not in the effect below, because React runs child effects before
    parent ones. This hook is mounted at the app root, so an effect here would always run after
    a descendant's: the banner's `GFM interest shown` fires on its own mount and would miss the
    context every time. A parent's render happens before any child effect, so this ordering is
    the right way round. The write is idempotent, so a double render under StrictMode is fine.
  */
  setGfmExperimentVariant(ready ? toGfmExperimentVariant(value) : null)

  useEffect(() => {
    /*
      `ready` drops back to false whenever the account changes or is cleared, including when
      AuthContextController dispatches SET_UNAUTHORISED after a failed getUserData. The render
      above has already cleared the published variant by then, which is what stops the previous
      user's riding along on the login page views and the next user's identify.
    */
    if (!ready) {
      firedFor.current = null

      return
    }

    const variant = toGfmExperimentVariant(value)

    if (!variant) {
      return
    }

    const key = exposureKey(accountId, variant)

    // Compared against the variant it fired for, not a bare boolean: logging in as a different
    // account happens without a page reload, and that second user needs their own exposure.
    if (firedFor.current === key || hasAlreadyFired(key)) {
      return
    }

    firedFor.current = key
    recordFired(key)

    // The render above has already published it, so this event carries its own experiment context
    trackRef.current(GFM_EXPERIMENT_EVENTS.exposed, {
      experimentName: GFM_EXPERIMENT_NAME,
      experimentVariant: variant
    })
  }, [ready, value, accountId])
}
